import { getTextDelta } from '@databases/react/useTextDelta';
import { type SimpleStore } from '@databases/redux-types';
import {
  type Database,
  type DatabaseField,
  type DatabaseEntry,
  type DatabaseValue,
  DatabaseFieldType,
  type PageLabelsDelta
} from '@databases/types/database';
import {
  type ValueChange,
  type MultiValueFieldType,
  MultiValueChangeType
} from '@databases/utils/database/changesets';
import {
  getReferencedLabelContentRefs,
  getContentLabels,
  applyPageLabels
} from '@databases/utils/database/field-types/pageLabels';
import {
  getReferencedStatusContentRefs,
  getContentStatus,
  DEFAULT_PAGE_STATUS_COLOR,
  applyPageStatus
} from '@databases/utils/database/field-types/pageStatus';
import { getConvertedDatabaseValue } from '@databases/utils/database/utils';
import { getLabelsDelta } from '@databases/utils/deltas/labels-delta';
import { normalizeText } from '@databases/utils/utils';
import { isEmpty } from 'lodash-es';

import { type DefaultRootState } from '../redux.ts';

/**
 * Returns the value change (delta) for the pasted value for the given field
 * or null if the value should not be applied or the field is read only.
 * For some fields, the value change is applied to the referenced content.
 **/
export function applyDeltaAfterPaste(
  {
    entry,
    field,
    value,
    database,
    text
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    value: DatabaseValue | undefined;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
): ValueChange[] | null {
  const previousDatabaseValue = getConvertedDatabaseValue(
    database,
    field,
    entry,
    getState()
  );
  switch (field.type) {
    // read only fields
    case DatabaseFieldType.JIRA_ISSUE_DETAILS:
    case DatabaseFieldType.ENTRY_DETAILS:
    case DatabaseFieldType.BACKLINK:
    case DatabaseFieldType.PAGE_DETAILS:
    case DatabaseFieldType.AI_SUMMARY: {
      return null;
    }

    // fields which has to be applied to referenced content
    case DatabaseFieldType.PAGE_LABELS: {
      replacePageLabels({ entry, field, database, text }, { getState, dispatch });
      return null;
    }
    case DatabaseFieldType.PAGE_STATUS: {
      replacePageStatus({ entry, field, value, database, text }, { getState, dispatch });
      return null;
    }

    // single value fields
    case DatabaseFieldType.TEXT: {
      const textValue = (value as DatabaseValue<DatabaseFieldType.TEXT>)?.value ?? {
        plain: ''
      };
      const richContent = textValue.rich?.content;
      const hasRichContent = Array.isArray(richContent) && richContent.length > 0;

      const delta = getTextDelta(
        (
          (previousDatabaseValue as DatabaseValue<DatabaseFieldType.TEXT>)?.value ?? {
            plain: ''
          }
        ).plain ?? '',
        textValue.plain ?? ''
      );

      if (hasRichContent && textValue.rich) {
        return [{ type: field.type, delta, adf: textValue.rich }];
      }
      return [{ type: field.type, delta }];
    }
    case DatabaseFieldType.NUMBER: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.NUMBER>)?.value
        }
      ];
    }
    case DatabaseFieldType.CHECKBOX: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.CHECKBOX>)?.value
        }
      ];
    }
    case DatabaseFieldType.DATE: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.DATE>)?.value
        }
      ];
    }

    // multi value fields
    case DatabaseFieldType.HYPERLINK: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
    case DatabaseFieldType.ENTRY_LINK: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
    case DatabaseFieldType.USER: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
    case DatabaseFieldType.JIRA_ISSUE: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
    case DatabaseFieldType.SELECT: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
    case DatabaseFieldType.PAGE_LINK: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
    case DatabaseFieldType.ATTACHMENT: {
      return replaceMultiFieldValue({
        field,
        databaseValue: value
      });
    }
  }
}

/**
 * Replace the value in a multi-select field
 * */
const replaceMultiFieldValue = ({
  field,
  databaseValue
}: {
  field: DatabaseField;
  databaseValue?: DatabaseValue;
}): ValueChange[] => {
  const isMultiValueFieldType = (
    value?: DatabaseValue,
    type?: DatabaseFieldType
  ): value is DatabaseValue<MultiValueFieldType | DatabaseFieldType.HYPERLINK> => {
    return value?.type === type;
  };

  const isMultiValue = isMultiValueFieldType(databaseValue, field.type);
  if (!isMultiValue) {
    return [];
  }
  const values = Array.isArray(databaseValue.value)
    ? databaseValue.value
    : [databaseValue.value];

  return [
    {
      type: field.type,
      delta: {
        type: MultiValueChangeType.SET_TO,
        value: values
      }
    }
  ] as ValueChange[];
};

const replacePageLabels = (
  {
    entry,
    field,
    database,
    text
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
) => {
  getReferencedLabelContentRefs({ database, field, entry }, getState())?.map(
    (contentRef) => {
      const existingLabels = getContentLabels(contentRef, {}, getState()) ?? [];
      const normalizedLabelsArray = normalizeText(text)?.split(' ') || [];
      const newLabels = isEmpty(text) ? [] : normalizedLabelsArray;
      const labelsToRemove = existingLabels.filter((label) => !newLabels.includes(label));
      const deltas: PageLabelsDelta = { add: [], remove: [] };

      newLabels.forEach((label) => {
        const delta = getLabelsDelta({
          action: 'select-option',
          option: { value: label, label }
        });
        if (delta) {
          deltas.add.push(...delta.add);
        }
      });

      labelsToRemove.forEach((label) => {
        const delta = getLabelsDelta({
          action: 'deselect-option',
          option: { value: label, label }
        });
        if (delta) {
          deltas.remove.push(...delta.remove);
        }
      });

      if (field.settings.allowEdit) {
        applyPageLabels(contentRef, deltas, { getState, dispatch });
      }
    }
  );
};

const replacePageStatus = (
  {
    entry,
    field,
    database,
    value,
    text
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    value: DatabaseValue | undefined;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
) => {
  const newValue = (value as DatabaseValue<DatabaseFieldType.PAGE_STATUS>)?.value;
  getReferencedStatusContentRefs({ database, field, entry }, getState())?.map(
    (contentRef) => {
      const existingStatus =
        newValue && getContentStatus(contentRef, newValue, getState());
      if (text && field.settings.allowEdit && existingStatus?.name !== text) {
        const newStatus = {
          name: text,
          color: DEFAULT_PAGE_STATUS_COLOR
        };
        applyPageStatus(contentRef, newStatus, { getState, dispatch });
      }
    }
  );
};
